import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import {
  Plus,
  Loader2,
  MoreVertical,
  Trash2,
  ExternalLink,
  Building2,
  DollarSign,
} from 'lucide-react'
import {
  dealsApi,
  campaignsApi,
  type Deal,
  type DealStage,
} from '@/services/api'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  DealStageBadge,
  DEAL_STAGE_CONFIG,
  DEAL_STAGES,
  ACTIVE_DEAL_STAGES,
} from '@/components/deal-stage-badge'

export default function DealsPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [page] = useState(1)
  const [stageFilter, setStageFilter] = useState<DealStage | 'all'>('all')
  const [campaignFilter, setCampaignFilter] = useState<string>('all')
  const [search, setSearch] = useState('')

  const { data: stageCounts } = useQuery({
    queryKey: ['deal-stage-counts', campaignFilter],
    queryFn: () =>
      dealsApi.getStageCounts(
        campaignFilter === 'all' ? undefined : campaignFilter
      ),
  })

  const { data: summary } = useQuery({
    queryKey: ['deal-summary', campaignFilter],
    queryFn: () =>
      dealsApi.getSummary(
        campaignFilter === 'all' ? undefined : campaignFilter
      ),
  })

  const { data: dealsData, isLoading } = useQuery({
    queryKey: [
      'deals',
      page,
      stageFilter,
      campaignFilter,
      search,
    ],
    queryFn: () =>
      dealsApi.list({
        page,
        per_page: 50,
        stage: stageFilter === 'all' ? undefined : stageFilter,
        campaign_id:
          campaignFilter === 'all' ? undefined : campaignFilter,
        search: search || undefined,
      }),
  })

  const { data: campaigns } = useQuery({
    queryKey: ['campaigns'],
    queryFn: () => campaignsApi.list({ per_page: 100 }),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => dealsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deals'] })
      queryClient.invalidateQueries({ queryKey: ['deal-stage-counts'] })
      queryClient.invalidateQueries({ queryKey: ['deal-summary'] })
    },
  })

  const updateStageMutation = useMutation({
    mutationFn: ({ id, stage }: { id: string; stage: DealStage }) =>
      dealsApi.update(id, { stage }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deals'] })
      queryClient.invalidateQueries({ queryKey: ['deal-stage-counts'] })
      queryClient.invalidateQueries({ queryKey: ['deal-summary'] })
    },
  })

  const deals = dealsData?.items ?? []
  const totalValue = summary?.total_expected_value ?? 0

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Deals</h1>
          <p className="text-muted-foreground">
            Track your sales pipeline and deals
          </p>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-5">
        {ACTIVE_DEAL_STAGES.map((stage) => {
          const config = DEAL_STAGE_CONFIG[stage]
          const count = stageCounts?.[stage] ?? 0
          return (
            <Card
              key={stage}
              className="cursor-pointer hover:border-primary/50 transition-colors"
              onClick={() =>
                setStageFilter(stageFilter === stage ? 'all' : stage)
              }
            >
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  {config.label}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2">
                  <config.icon className="h-5 w-5 text-muted-foreground" />
                  <span className="text-2xl font-semibold">{count}</span>
                </div>
              </CardContent>
            </Card>
          )
        })}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Pipeline Value
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-green-500" />
              <span className="text-2xl font-semibold">
                {Number(totalValue).toLocaleString()}
              </span>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>All Deals</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4">
            <Input
              placeholder="Search deals..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="max-w-xs"
            />
            <Select
              value={campaignFilter}
              onValueChange={setCampaignFilter}
            >
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Filter by campaign" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Campaigns</SelectItem>
                {campaigns?.items.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Tabs
              value={stageFilter}
              onValueChange={(v) => setStageFilter(v as DealStage | 'all')}
            >
              <TabsList>
                <TabsTrigger value="all">All</TabsTrigger>
                {DEAL_STAGES.map((stage) => (
                  <TabsTrigger key={stage} value={stage}>
                    {DEAL_STAGE_CONFIG[stage].label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : deals.length === 0 ? (
            <div className="py-8 text-center">
              <Building2 className="h-12 w-12 mx-auto text-muted-foreground/50" />
              <h3 className="mt-4 text-lg font-medium">No deals yet</h3>
              <p className="mt-1 text-muted-foreground">
                Deals are created automatically when outreach emails are sent
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Deal</TableHead>
                  <TableHead>Company</TableHead>
                  <TableHead>Stage</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead className="text-right">Value</TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {deals.map((deal) => (
                  <TableRow
                    key={deal.id}
                    className="cursor-pointer"
                    onClick={() => navigate(`/app/deals/${deal.id}`)}
                  >
                    <TableCell>
                      <div className="font-medium">{deal.title}</div>
                      {deal.campaign_name && (
                        <div className="text-xs text-muted-foreground">
                          {deal.campaign_name}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      {deal.company?.name || 'Unknown'}
                      {deal.company?.website && (
                        <div className="text-xs text-muted-foreground">
                          {deal.company.website}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <DealStageBadge stage={deal.stage} size="sm" />
                    </TableCell>
                    <TableCell>
                      {deal.contact_name && (
                        <div className="text-sm">{deal.contact_name}</div>
                      )}
                      {deal.contact_email && (
                        <div className="text-xs text-muted-foreground">
                          {deal.contact_email}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {deal.expected_value != null ? (
                        <span className="font-medium">
                          ${Number(deal.expected_value).toLocaleString()}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {DEAL_STAGES.filter((s) => s !== deal.stage).map(
                            (stage) => (
                              <DropdownMenuItem
                                key={stage}
                                onClick={() =>
                                  updateStageMutation.mutate({
                                    id: deal.id,
                                    stage,
                                  })
                                }
                              >
                                Move to {DEAL_STAGE_CONFIG[stage].label}
                              </DropdownMenuItem>
                            )
                          )}
                          <DropdownMenuItem
                            className="text-destructive"
                            onClick={() => deleteMutation.mutate(deal.id)}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
